import { FlexRow } from '@/components/common/Flex';
import { Shield01, TrendUp01 } from '@/components/common/icons';
import { InfoButtonWithModal } from '@/components/common/InfoModal';
import { Header, Paragraph, Text } from '@/components/common/Typography';
import {
    FlexProps,
    Slider,
    SliderFilledTrack,
    SliderThumb,
    SliderTrack,
} from '@chakra-ui/react';
import {
    iconByGoalAccountInvestmentType,
    titleByGoalAccountInvestmentType,
} from '@common/react/goals';
import { useCurrentRateInfo } from '@common/react/rates';
import { GoalAccountInvestmentType } from '@common/types';
import { FlexCol } from '../common/Flex';
import Icon from '../common/icons/Icon';
import {
    CashPlusDescription,
    CashPlusLabel,
    ClassicPortfolioDescription,
    ClassicPortfolioLabel,
    PremiumPortfolioDescription,
    PremiumPortfolioLabel,
} from './detail/InvestmentTypes';
import {
    GoalSectorsPieChart,
    GoalSectorsPieChartLegend,
} from './GoalSectorsPieChart';
import {
    getAnnualReturnInfoForRiskScore,
    RiskLabel,
    riskLabelByRiskScore,
} from './helpers';

interface RiskSelectionContentProps {
    title?: string;
    riskScore: number;
    /**
     * IMPORTANT: Only pass recommended risk scores from Atomic!
     * If none is available, pass `null` or `undefined`.
     */
    recommendedRiskScore: number | null | undefined;
    minRiskScore: 0 | 1;
    onChangeRiskScore: (riskScore: number) => void;
    investmentType: GoalAccountInvestmentType;
}

// TODO: Move this into a separate file
export const RiskSelectionContent = ({
    title,
    riskScore,
    recommendedRiskScore,
    minRiskScore,
    onChangeRiskScore,
    investmentType,
}: RiskSelectionContentProps) => {
    const { currentRateInfo } = useCurrentRateInfo();
    if (!currentRateInfo) return null;

    const isRecommended = riskScore === recommendedRiskScore;
    const riskLabel = riskLabelByRiskScore[riskScore];

    if (riskScore < minRiskScore) {
        // This should never happen
        console.warn(`riskScore ${riskScore} < minRiskScore ${minRiskScore}`);
        return null;
    }

    const {
        annualReturnLabelCapitalized,
        annualReturnStr,
        annualReturnDisclosure,
    } = getAnnualReturnInfoForRiskScore({
        investmentType,
        riskScore,
        currentRateInfo,
    });

    const iconName = iconByGoalAccountInvestmentType[investmentType];
    const investmentTypeTitle =
        titleByGoalAccountInvestmentType[investmentType];

    const pieChartDescription = `${investmentTypeTitle} (${riskLabel.toLowerCase()})`;

    return (
        <>
            <FlexRow grow={1}>
                <FlexCol grow={1} basis={0} mr={8}>
                    <Header mb={2}>{title}</Header>
                    <Paragraph
                        color="textMuted"
                        minH={44} // prevent layout shift when switching risk
                    >
                        {descriptionByRiskLabel[riskLabel]}
                    </Paragraph>
                    <FlexRow gap={6} mt={2}>
                        <FlexCol
                            justify="flex-end"
                            minW={28} // prevent layout shift when switching risk
                        >
                            <Text
                                color="green.700"
                                fontSize="xs"
                                fontWeight="semibold"
                                letterSpacing="widest"
                                mb={4}
                                opacity={isRecommended ? 1 : 0}
                                transition="opacity"
                                transitionDuration="200ms"
                            >
                                RECOMMENDED
                            </Text>
                            <Header color="gray.400" mb={2}>
                                <Text fontSize="3xl" color="blue.500">
                                    {riskScore}
                                </Text>
                                {' / 10'}
                            </Header>
                            <Text fontSize="lg">{riskLabel}</Text>
                        </FlexCol>
                        <FlexCol justify="flex-end">
                            <Text
                                fontSize="sm"
                                color="textMuted"
                                whiteSpace="nowrap"
                                mb={2}
                            >
                                Investment type
                                <InfoButtonWithModal
                                    buttonProps={{
                                        h: 4,
                                        w: 4,
                                        onClickEventParams: {
                                            location: 'Goal Risk Selection',
                                        },
                                    }}
                                    title="Plenty investment types"
                                >
                                    <FlexCol>
                                        <CashPlusLabel mb={2} />
                                        <CashPlusDescription mb={8} />
                                        <PremiumPortfolioLabel mb={2} />
                                        <PremiumPortfolioDescription mb={8} />
                                        <ClassicPortfolioLabel mb={2} />
                                        <ClassicPortfolioDescription />
                                    </FlexCol>
                                </InfoButtonWithModal>
                            </Text>
                            <FlexRow alignItems="center">
                                <Icon name={iconName} size="sm" mr={2} />
                                <Text fontSize="lg" whiteSpace="nowrap">
                                    {investmentTypeTitle}
                                </Text>
                            </FlexRow>
                        </FlexCol>
                        {riskScore === 0 && (
                            <FlexCol justify="flex-end">
                                <Text
                                    fontSize="sm"
                                    color="textMuted"
                                    whiteSpace="nowrap"
                                    mb={2}
                                >
                                    {annualReturnLabelCapitalized}
                                    <InfoButtonWithModal
                                        buttonProps={{
                                            h: 4,
                                            w: 4,
                                            onClickEventParams: {
                                                location: 'Goal Risk Selection',
                                            },
                                        }}
                                        title={annualReturnLabelCapitalized}
                                    >
                                        {annualReturnDisclosure}
                                    </InfoButtonWithModal>
                                </Text>
                                <Text fontSize="lg">{annualReturnStr}</Text>
                            </FlexCol>
                        )}
                    </FlexRow>
                </FlexCol>
                <PieChartSection
                    investmentType={investmentType}
                    riskScore={riskScore}
                    description={pieChartDescription}
                />
            </FlexRow>
            <RiskSlider
                value={riskScore}
                onChange={onChangeRiskScore}
                min={minRiskScore}
            />
        </>
    );
};

const descriptionByRiskLabel: Record<RiskLabel, string> = {
    'Least risky':
        'Cash+ Portfolios are the lowest risk investment available. It’s a portfolio backed by government money market funds, meaning 99.5%+ of the portfolio is in US-government backed treasuries.',
    'Less risky':
        'Less risky investments have a higher amount of US treasuries and bonds. These tend to have lower investment volatility (the value goes up and down relatively less), and there tends to be a lower return as a result. The federal funds rate more directly influence returns. These are best for near term goals where you may need the money soon and don’t want to risk the investment decreasing in value.',
    'Balanced risk':
        'Balanced risk investments have a higher amount of stocks. This means your portfolios will be more volatile (the value goes up and down more in the short term), and you may be able to earn a higher return as a result. These are best for medium term goals where you can wait a short amount of time for the market to go back up, or can save more to replace any drop in investment value.',
    'More risky':
        'More risky investments have the highest amounts of stocks. This means your portfolios will be volatile (the value will go up and down in the short term), and you may be able to earn a higher return as a result. These are best for long term goals where you can wait for the market to go back up, or for medium term goals where you can easily save enough to replace any unexpected drop in value.',
};

interface PieChartSectionProps extends FlexProps {
    investmentType: GoalAccountInvestmentType;
    riskScore: number;
    description?: string;
}

export const PieChartSection = ({
    investmentType,
    riskScore,
    description,
    ...props
}: PieChartSectionProps) => {
    return (
        <FlexRow w={80} {...props}>
            <FlexCol w="full" align="center">
                <GoalSectorsPieChart
                    investmentType={investmentType}
                    riskScore={riskScore}
                />
                {description && (
                    <Text fontWeight="semibold" mt={6}>
                        {description}
                    </Text>
                )}
                <GoalSectorsPieChartLegend
                    investmentType={investmentType}
                    riskScore={riskScore}
                    layoutDirection="horizontal"
                    mt={8}
                />
            </FlexCol>
        </FlexRow>
    );
};

interface RiskSliderProps extends Omit<FlexProps, 'onChange'> {
    value: number;
    onChange?: (value: number) => void;
    min?: number;
    readOnly?: boolean;
}

export const RiskSlider = ({
    value,
    onChange,
    min = 0,
    readOnly = false,
    ...props
}: RiskSliderProps) => {
    return (
        <FlexRow
            w="full"
            mt={12}
            py={4}
            px={6}
            gap={6}
            bg="gray.50"
            borderRadius="xl"
            {...props}
        >
            <FlexCol alignItems="center" gap={2}>
                <Shield01 color="textMuted" />
                <Text fontSize="sm" color="textMuted" textAlign="center">
                    Lowest
                    <br />
                    risk
                </Text>
            </FlexCol>
            <FlexRow grow={1}>
                <Slider
                    value={value}
                    onChange={onChange}
                    min={min}
                    max={10}
                    step={1}
                >
                    <SliderTrack bg="gray.200">
                        <SliderFilledTrack bg="blue.400" />
                    </SliderTrack>
                    {!readOnly && <SliderThumb boxSize={5} bg="blue.400" />}
                </Slider>
            </FlexRow>
            <FlexCol alignItems="center" gap={2}>
                <TrendUp01 color="textMuted" />
                <Text fontSize="sm" color="textMuted" textAlign="center">
                    Higher
                    <br />
                    risk
                </Text>
            </FlexCol>
        </FlexRow>
    );
};
